/**
 * Waitlist API client.
 * Mirrors PRD §7 / tech design §6.4 contracts.
 */

export type WaitlistStatus = 'WAITING' | 'NOTIFIED' | 'EXPIRED';

export interface WaitlistEntry {
  id: string;
  sessionId: string;
  childrenCount: number;
  adultsCount: number;
  capacityDemand: number;
  status: WaitlistStatus;
  position: number;
  createdAt: string;
  notifiedAt: string | null;
}

export interface WaitlistMineEntry {
  id: string;
  status: WaitlistStatus;
  childrenCount: number;
  adultsCount: number;
  capacityDemand: number;
  position: number;
  createdAt: string;
  notifiedAt: string | null;
  session: {
    id: string;
    startTime: string;
    endTime: string;
    freeSpots: number;
    isCancelled: boolean;
    activity: {
      id: string;
      slug: string;
      titleCN: string | null;
      titleEN: string | null;
      location: string | null;
      coverImageUrl: string | null;
    };
  };
}

export interface SessionWaitlistStatus {
  sessionId: string;
  isFull: boolean;
  isWithinCutoff: boolean;
  isCancelled: boolean;
  waitlistCount: number;
  userEntry: WaitlistEntry | null;
}

export interface AdminWaitlistEntry {
  id: string;
  sessionId: string;
  status: WaitlistStatus;
  position: number;
  childrenCount: number;
  adultsCount: number;
  capacityDemand: number;
  notifiedAt: string | null;
  lastNotifyAttemptAt: string | null;
  notifyAttempts: number;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    phone: string | null;
  };
}

export interface ClaimDetail {
  waitlistId: string;
  status: WaitlistStatus;
  childrenCount: number;
  adultsCount: number;
  capacityDemand: number;
  isWithinCutoff: boolean;
  canClaim: boolean;
  /** True when a notification email was ever sent (survives requeue to WAITING). */
  wasNotified: boolean;
  /** 0-based count of active entries ahead in the queue; display ordinal = position + 1. */
  position: number;
  /** False once the activity is no longer APPROVED (entry will expire shortly). */
  activityActive: boolean;
  activity: {
    id: string;
    slug: string;
    titleCN: string | null;
    titleEN: string | null;
    location: string | null;
  };
  session: {
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    freeSpots: number;
    isCancelled: boolean;
  };
}

export interface WaitlistApiError extends Error {
  code?: string;
  status?: number;
}

function makeError(message: string, code?: string, status?: number): WaitlistApiError {
  const err = new Error(message) as WaitlistApiError;
  err.code = code;
  err.status = status;
  return err;
}

class WaitlistApi {
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  }

  private async request<T>(
    path: string,
    init: RequestInit & { token?: string }
  ): Promise<T | null> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((init.headers as Record<string, string>) || {}),
    };
    if (init.token) headers.Authorization = `Bearer ${init.token}`;

    const response = await fetch(`${this.apiUrl}${path}`, { ...init, headers });
    if (response.status === 204) return null;

    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const errBody = body?.error || {};
      throw makeError(
        errBody.message || `Request failed: ${response.status}`,
        errBody.code,
        response.status
      );
    }
    return (body?.data ?? null) as T | null;
  }

  async join(
    token: string,
    body: { sessionId: string; childrenCount: number; adultsCount: number }
  ): Promise<WaitlistEntry> {
    const result = await this.request<WaitlistEntry>('/waitlist', {
      method: 'POST',
      body: JSON.stringify(body),
      token,
    });
    if (!result) throw makeError('No data returned from join');
    return result;
  }

  async leave(token: string, waitlistId: string): Promise<void> {
    await this.request<null>(`/waitlist/${waitlistId}`, { method: 'DELETE', token });
  }

  async getMine(token: string): Promise<WaitlistMineEntry[]> {
    const result = await this.request<WaitlistMineEntry[]>('/waitlist/mine', {
      method: 'GET',
      token,
    });
    return result ?? [];
  }

  async getClaim(token: string, waitlistId: string): Promise<ClaimDetail> {
    const result = await this.request<ClaimDetail>(`/waitlist/${waitlistId}/claim`, {
      method: 'GET',
      token,
    });
    if (!result) throw makeError('Claim detail not found', 'WAITLIST_NOT_FOUND', 404);
    return result;
  }

  async getSessionStatus(sessionId: string, token?: string | null): Promise<SessionWaitlistStatus> {
    const result = await this.request<SessionWaitlistStatus>(
      `/sessions/${sessionId}/waitlist/status`,
      { method: 'GET', token: token || undefined }
    );
    if (!result) throw makeError('Session status not found');
    return result;
  }

  /** Admin: full waitlist for a session, including PII + session metadata. */
  async adminListForSession(
    token: string,
    sessionId: string
  ): Promise<{
    session: {
      id: string;
      startTime: string;
      endTime: string;
      maxCapacity: number;
      currentBookings: number;
      isCancelled: boolean;
      activity: { id: string; slug: string; titleCN: string | null; titleEN: string | null };
    };
    entries: AdminWaitlistEntry[];
  } | null> {
    return this.request(`/admin/waitlist?sessionId=${encodeURIComponent(sessionId)}`, {
      method: 'GET',
      token,
    });
  }

  /** Host: count of waiting entries on a session (no PII). */
  async hostCount(token: string, sessionId: string): Promise<{ count: number }> {
    const result = await this.request<{ count: number }>(`/host/sessions/${sessionId}/waitlist`, {
      method: 'GET',
      token,
    });
    return result ?? { count: 0 };
  }
}

export const waitlistApi = new WaitlistApi();
